import { API } from "../apiService";

export type UserRecordState = "present" | "missing" | "unknown";

interface UserRecordStateOptions {
	attempts?: number;
}

/**
 * A sign-in identity can exist without a matching user document. The Cognito (or Google) account is
 * created at /register, but the backend record is only written at /creating-profile — after
 * onboarding. Anything that interrupts the user in between leaves the two out of sync: closing the
 * tab, switching device, or following the Cognito verification link, which arrives at signUp time
 * while onboarding is still on screen.
 *
 * That state is otherwise terminal. Signing in bounces, because every authenticated call 401s and
 * the response interceptor redirects to /signin; registering again reports the email as taken. So we
 * check for the record right after sign-in and route those users back through onboarding to finish
 * creating it.
 */
export async function getUserRecordState({ attempts = 1 }: UserRecordStateOptions = {}): Promise<UserRecordState> {
	const maximumAttempts = Math.max(1, attempts);

	for (let attempt = 1; attempt <= maximumAttempts; attempt += 1) {
		try {
			// GET /users/account answers this without throwing: the backend returns 200 with an empty body
			// when the token is valid but no document matches the address.
			const { data } = await API.get("users/account", { authFailureMode: "preserve" });
			return data?._id ? "present" : "missing";
		} catch {
			// Retry indeterminate checks when requested. A confirmed empty response still means the user
			// record is missing and should not be retried here.
			if (attempt === maximumAttempts) return "unknown";
		}
	}

	return "unknown";
}

/**
 * Lenient reading for sign-in: never block someone on a failed check — a network blip should let
 * them through to their normal destination rather than divert them into onboarding.
 */
export async function hasUserRecord(): Promise<boolean> {
	return (await getUserRecordState()) !== "missing";
}
